import * as React from "react";
import { FC } from "react";
import { styled } from "@mui/material/styles";
import Paper from "@mui/material/Paper";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup, {
  toggleButtonGroupClasses,
  ToggleButtonGroupProps,
} from "@mui/material/ToggleButtonGroup";
import { SystemStyleObject } from "@mui/system";
import { Typography, useTheme } from "@mui/material";
import { useResponsiveContext } from "@context/ResponsiveContext";

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  [`& .${toggleButtonGroupClasses.grouped}`]: {
    margin: theme.spacing(0.5),
    border: 0,
    borderRadius: theme.shape.borderRadius,
    [`&.${toggleButtonGroupClasses.disabled}`]: {
      border: 0,
    },
  },
  [`& .${toggleButtonGroupClasses.middleButton},& .${toggleButtonGroupClasses.lastButton}`]:
    {
      marginLeft: -1,
      borderLeft: "1px solid transparent",
    },
}));

interface ButtonConfig {
  id: string;
  icon: FC<{ color: string }>;
  label: string;
  disabled?: boolean;
}

const ToggleActionButtonGroup: FC<{
  buttons: ButtonConfig[];
  selectedId: string;
  onSelectedIdChange: (newId: string) => void;
  showSelectedValue?: boolean;
  selectedValuePrefix?: string;
  hideButtonLabels?: boolean;
  sx?: SystemStyleObject;
  options?: ToggleButtonGroupProps;
}> = ({
  buttons,
  selectedId,
  onSelectedIdChange,
  showSelectedValue,
  selectedValuePrefix,
  hideButtonLabels = false,
  sx,
  options,
}) => {
  const { onMobile } = useResponsiveContext();
  const { palette } = useTheme();

  return (
    <Paper
      elevation={0}
      sx={(theme) => ({
        display: "flex",
        flexDirection: "column",
        border: `1px solid ${theme.palette.divider}`,
        flexWrap: "wrap",
        marginY: 1,
        paddingX: 1,
        backgroundColor:
          theme.palette.mode === "dark"
            ? "#292929"
            : theme.palette.background.paper,
        ...sx,
      })}
    >
      <StyledToggleButtonGroup
        size="small"
        exclusive
        {...options}
        value={selectedId}
        onChange={(event, newId) => newId && onSelectedIdChange(newId)}
      >
        {buttons.map((button) => {
          const ButtonIcon = button.icon;
          return (
            <ToggleButton
              key={button.id}
              value={button.id}
              sx={{
                display: "flex",
                flexDirection: "column",
                padding: onMobile ? "4px" : "6px 8px",
                flex: 1,
                minWidth: "10vw",
                border:
                  selectedId === button.id && palette.mode === "dark"
                    ? "1px solid #ffffff1f !important"
                    : undefined,
              }}
              disabled={button.disabled}
            >
              <ButtonIcon
                color={
                  button.disabled ? palette.text.disabled : palette.text.primary
                }
              />
              {!hideButtonLabels && (
                <Typography
                  variant={"caption"}
                  textAlign={"center"}
                  maxWidth={"100%"}
                  whiteSpace={"nowrap"}
                  overflow={"hidden"}
                  textOverflow={"ellipsis"}
                  textTransform={"none"}
                  color={
                    button.disabled
                      ? palette.text.disabled
                      : palette.text.primary
                  }
                >
                  {button.label}
                </Typography>
              )}
            </ToggleButton>
          );
        })}
      </StyledToggleButtonGroup>
      {showSelectedValue && (
        <Typography
          variant={"caption"}
          textAlign={"center"}
          maxWidth={"100%"}
          whiteSpace={"nowrap"}
          overflow={"hidden"}
          textOverflow={"ellipsis"}
          textTransform={"none"}
        >
          {selectedValuePrefix}
          {buttons.find((button) => button.id === selectedId)?.label}
        </Typography>
      )}
    </Paper>
  );
};

export default ToggleActionButtonGroup;
